import { inject, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../core/services/language.service';
import { MenuItemAction } from '../../shared/models/master.models';
import {
	AuthChannel,
	DEMAND_LEVEL,
	IDocInfo,
	ITermsAndConditionsForm,
	ITextLocaleMap,
	IWorkflowDetailResponse,
	OCR_DOC_COUNTRY_TYPE,
} from '../models/workflows.models';
import {
	DOCS_TYPE,
	IConnectionOperation,
	IDocumentsDocs,
	IFormOperation,
	IOcrOperation,
	IOperation,
	IPolOperation,
	OPERATION_TYPE,
	POL_TEST,
} from '../../operations/models/operations.models';
import { DELAYED_WARNING_DEFAULT_TEXT_KEY_BY_LOCALE } from '../constants/workflow-form-defaults.constants';
import {
	hasWorkflowLocaleText,
	mergeEmptyWorkflowLocalesFromDefaults,
	mergeGenericDocLocaleField,
	normalizeWorkflowLocaleText,
} from '../utils/workflow-locale-text.util';
import { resolveSelectIdOrPlainValue } from '../utils/workflow-form-control.util';
import { findAttorneyPowersSources, mapAttorneyPowersFormPatch } from '../utils/workflow-attorney-powers.util';
import { mapConnectionsFormPatch } from '../utils/workflow-connections.util';
import {
	findConnectionsWithoutEval,
	findNonScriptureDocumentsOperation,
	normalizeDocsMatchData,
	normalizeDocumentMethodForForm,
} from '../utils/workflow-documents.util';
import { findFinishOperation, normalizeFinishRedirectFromApi } from '../utils/workflow-finish.util';
import { findHomeOperation } from '../utils/workflow-home.util';
import { patchFormStepFromApi, readFormStepsFromConfig } from '../utils/workflow-form.util';
import {
	getOcrDocCountryType,
	getOcrFraudScoringType,
	mapOcrFraudCheckFromApi,
	OCR_FRAUD_CHECK_KEYS,
	ocrObfuscationSelectedFromFields,
} from '../utils/workflow-ocr.util';
import { resolveOcrSchengenUnlimited } from '../utils/company-country.util';
import { hasRetentionOverride } from '../../shared/utils/retention-duration.util';
import { mapPolScoreToFormThreshold, sanitizePolSetting } from '../utils/workflow-pol.util';
import { parseValidationTypeFromDetail } from '../utils/workflow-validation.util';
import { WorkflowFormDefaultsService } from './workflow-form-defaults.service';
import { WorkflowsHelperService } from './workflows-helper.service';

type DelayedWarningVisibility = 'start_and_resume' | 'resume_only';
type DelayedWarningMessageType = 'default' | 'custom';

/**
 * Contrato que el componente del editor implementa para que el mapper pueda:
 * - mutar FormArrays (pasos FORM, docs)
 * - invocar callbacks UI (`formDoc` / `formCheck`)
 * - sincronizar flags MiDNI / validación por compañía tras el patch
 *
 * No incluye HTTP ni loading: eso queda en el componente.
 */
export interface WorkflowFormDetailHost {
	/** Form reactivo completo del editor. */
	form: FormGroup;
	/** FormArray de pasos FORM (`operations.FORM.steps`). */
	get formStepsArray(): FormArray;
	/** Añade/edita una fila de documento en el FormArray de DOCUMENTS. */
	formDoc(info: { form: any; index?: number }): void;
	/** Añade/edita un policy check de HOME (términos). */
	formCheck(response: { mode: MenuItemAction; form?: ITermsAndConditionsForm; index?: number }): any;
	/** Caché del texto custom del aviso diferido (al cambiar tipo default/custom). */
	delayedWarningCustomTextCache: Record<string, string>;
	/** Último tipo de mensaje de aviso diferido aplicado (`default` | `custom`). */
	lastDelayedWarningMessageType: string;
	/**
	 * `true` mientras se hace `patchValue` del detalle.
	 * Evita que effects (MiDNI) reaccionen a mitad de hidratación.
	 */
	applyingWorkflowDetail: boolean;
	/** Snapshot previo de elegibilidad España/DNI para no pisar MiDNI al cargar. */
	prevSpainAllowedForMidni: boolean;
	isSpainAllowedForDni(): boolean;
	syncSkipPolWhenMidniAndPol(): void;
	applyWorkflowValidationForCompany(): void;
	/** Tras hidratar OCR, alinear `is_schengen_unlimited` con el país de la compañía. */
	syncSchengenUnlimitedForCompany(): void;
}

/**
 * Hidrata el FormGroup del editor a partir del detalle de workflow (NIF-530).
 *
 * Abrir cuando: hidratar form desde GET detalle (`applyDetail`).
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormDetailMapperService {
	private langSvc = inject(LanguageService);
	private workflowFormDefaults = inject(WorkflowFormDefaultsService);
	private workflowsHelper = inject(WorkflowsHelperService);
	private trans = inject(TranslateService);

	/**
	 * Alias público: ISelect/`id` o string plano → id tipado.
	 * Delegado a `resolveSelectIdOrPlainValue` (el componente aún llama `getToggleValue`).
	 */
	public getToggleValue<T extends string>(value: unknown, fallback: T): T {
		return resolveSelectIdOrPlainValue(value, fallback);
	}

	/**
	 * Textos i18n por defecto del aviso de proceso diferido (claves en constants).
	 *
	 * @param locales - Idiomas permitidos del workflow
	 * @returns Mapa locale → texto traducido
	 */
	public getDelayedWarningDefaultTextMap(locales: readonly string[]): Record<string, string> {
		return locales.reduce(
			(acc, locale) => {
				const defaultTextKey =
					DELAYED_WARNING_DEFAULT_TEXT_KEY_BY_LOCALE[locale] ||
					'WORKFLOWS.FORM.GENERAL.ALERT_POSTERGADO_DEFAULT_TEXT';
				acc[locale] = this.trans.instant(defaultTextKey);
				return acc;
			},
			{} as Record<string, string>,
		);
	}

	/**
	 * Mapa locale → `''` para resetear el aviso custom al volver a tipo `default`.
	 */
	public getDelayedWarningEmptyTextMap(locales: readonly string[]): Record<string, string> {
		return locales.reduce(
			(acc, locale) => {
				acc[locale] = '';
				return acc;
			},
			{} as Record<string, string>,
		);
	}

	/**
	 * Lee visibilidad del aviso diferido desde `config` o `metadata` legacy.
	 * Prioriza `resume_only` si aparece en cualquiera de las dos fuentes.
	 */
	public parseDelayedWarningSettings(config: IWorkflowDetailResponse['config'] | undefined): {
		visibility: DelayedWarningVisibility;
	} {
		const metadataVisibility = config?.metadata?.['delayed_process_warning_visibility'];
		return {
			visibility:
				metadataVisibility === 'resume_only' || config?.delayed_process_warning_visibility === 'resume_only'
					? 'resume_only'
					: 'start_and_resume',
		};
	}

	/**
	 * Canal 2FA del workflow: prioriza `config.tfa.type`; si no, `metadata.two_factor_channel`.
	 *
	 * @returns `'sms' | 'whatsapp' | 'none'`
	 */
	public resolveTwoFactorWorkflowChannel(detail: IWorkflowDetailResponse): AuthChannel {
		const channelFromTfa = detail?.config?.tfa?.type;
		if (channelFromTfa === 'sms' || channelFromTfa === 'whatsapp') {
			return channelFromTfa;
		}
		const metadata = detail?.config?.metadata as Record<string, unknown> | undefined;
		const channelFromMetadata = metadata?.['two_factor_channel'];
		if (channelFromMetadata === 'sms' || channelFromMetadata === 'whatsapp') {
			return channelFromMetadata;
		}
		return 'none';
	}

	/**
	 * Punto de entrada: hidrata `general` + cada operación desde el detalle API.
	 *
	 * Efectos colaterales en el host:
	 * - `applyingWorkflowDetail` around del `patchValue` (protege effects MiDNI)
	 * - caché del aviso diferido
	 * - `syncSkipPolWhenMidniAndPol` + `applyWorkflowValidationForCompany`
	 *
	 * @param detail - Respuesta GET perfil/workflow
	 * @param host - Componente (form + callbacks)
	 */
	public applyDetail(detail: IWorkflowDetailResponse, host: WorkflowFormDetailHost): void {
		const delayedSettings = this.parseDelayedWarningSettings(detail.config);
		// Si el API trae texto custom en algún locale → tipo `custom`; si no, defaults i18n.
		const hasCustomWarningText = hasWorkflowLocaleText(detail.config?.delayed_process_warning_text);
		const delayedWarningMessageType: DelayedWarningMessageType = hasCustomWarningText ? 'custom' : 'default';
		const delayedWarningText =
			delayedWarningMessageType === 'default'
				? this.getDelayedWarningDefaultTextMap(this.langSvc.getAllowedLanguages())
				: normalizeWorkflowLocaleText(
						detail.config?.delayed_process_warning_text,
						this.langSvc.getAllowedLanguages(),
					);

		host.applyingWorkflowDetail = true;
		const parsedValidation = parseValidationTypeFromDetail(detail.config?.validation?.type);
		host.form.patchValue({
			general: {
				allowed_device: detail.config?.allowed_device,
				version: detail.config?.version,
				metadata: detail.config?.metadata,
				has_saved_state: detail.config?.has_saved_state ? 'true' : 'false',
				location: detail.config?.location !== 'disabled',
				notify: detail.config?.notify,
				required_recording: detail.config?.required_recording,
				include_images: detail.config?.include_images,
				token_duration_minutes: detail.config?.token_duration_minutes ?? 120,
				override_data_retention: hasRetentionOverride(detail.config),
				document_retention_days: detail.config?.document_retention_days ?? null,
				media_retention_days: detail.config?.media_retention_days ?? null,
				info_retention_days: detail.config?.info_retention_days ?? null,
				alias: detail.alias,
				api_key: detail.api_key,
				theme: detail.design?.uuid,
				delayed_process_warning: true,
				delayed_process_warning_visibility: delayedSettings.visibility,
				delayed_process_warning_text_type: delayedWarningMessageType,
				delayed_process_warning_text: delayedWarningText,
				two_factor_channel: this.resolveTwoFactorWorkflowChannel(detail),
				antifraud: {
					ip_block: detail.config?.antifraud?.ip_block,
					identifier_block: detail.config?.antifraud?.identifier_block,
					face_block: detail.config?.antifraud?.face_block,
					block_operation: detail.config?.antifraud?.block_operation,
					email_alert: detail.config?.antifraud?.email_alert,
					available_email_alert: Boolean(detail.config?.antifraud?.email_alert),
				},
				sdk: detail.config?.sdk,
				validation: parsedValidation.mode,
				validation_manual_type: parsedValidation.manualType,
				validation_manual_errors: parsedValidation.manualErrors,
				validation_email: detail.config?.validation?.emails,
			},
			operations: {
				[OPERATION_TYPE.HOME]: this.setFormHome(detail, host),
				[OPERATION_TYPE.FORM]: this.setFormForm(detail, host),
				[OPERATION_TYPE.OCR]: this.setFormOcr(detail),
				[OPERATION_TYPE.POL]: this.setFormPol(detail),
				[OPERATION_TYPE.ATTORNEY_POWERS]: this.setAttorneyPowersDocuments(detail),
				[OPERATION_TYPE.DOCUMENTS]: this.setFormDocuments(detail, host),
				[OPERATION_TYPE.CONNECTIONS]: this.setFormConnections(detail),
				[OPERATION_TYPE.FINISH]: this.setFormFinish(detail),
			},
			confirmation: {
				todo: true,
			},
		});
		host.delayedWarningCustomTextCache =
			delayedWarningMessageType === 'custom'
				? { ...(delayedWarningText as Record<string, string>) }
				: this.getDelayedWarningEmptyTextMap(this.langSvc.getAllowedLanguages());
		host.lastDelayedWarningMessageType = delayedWarningMessageType;
		host.form.updateValueAndValidity();
		host.prevSpainAllowedForMidni = host.isSpainAllowedForDni();
		host.applyingWorkflowDetail = false;
		host.syncSkipPolWhenMidniAndPol();
		host.syncSchengenUnlimitedForCompany();
		host.applyWorkflowValidationForCompany();
	}

	/**
	 * Normaliza texto multi-locale del API y rellena locales vacíos con defaults i18n (como en alta).
	 *
	 * @param value - String legacy o mapa por locale
	 * @param defaults - Defaults del `WorkflowFormDefaultsService`
	 */
	public normalizeDetailLocaleWithDefaults(
		value: string | ITextLocaleMap | null | undefined,
		defaults: Readonly<ITextLocaleMap>,
	): ITextLocaleMap {
		const allowed = this.langSvc.getAllowedLanguages();
		const n = normalizeWorkflowLocaleText(value, allowed);
		return mergeEmptyWorkflowLocalesFromDefaults(n, allowed, defaults);
	}

	/**
	 * Pautas de documento: si hay texto en algún idioma, completa el resto con defaults DOCS.*;
	 * si todo vacío, deja el mapa vacío (validación “when any filled” en el form).
	 */
	public mergeDocInstructionsIfAnyFilled(
		value: string | ITextLocaleMap | null | undefined,
		docType: string,
	): ITextLocaleMap {
		const allowed = this.langSvc.getAllowedLanguages();
		const n = normalizeWorkflowLocaleText(value, allowed);
		if (!hasWorkflowLocaleText(n)) {
			return n;
		}
		const defaults = this.workflowFormDefaults.getDocInstructionsDefaults(docType);
		return mergeEmptyWorkflowLocalesFromDefaults(n, allowed, defaults);
	}

	/**
	 * Título o descripción de fila documento/conexión: si hay texto en algún locale,
	 * rellena vacíos con copy i18n del tipo; si todo vacío, no fuerza defaults.
	 */
	public mergeDocRowTitleOrDescriptionIfAnyFilled(
		value: string | ITextLocaleMap | null | undefined,
		docType: string,
		field: 'title' | 'description',
	): ITextLocaleMap {
		const allowed = this.langSvc.getAllowedLanguages();
		const n = normalizeWorkflowLocaleText(value, allowed);
		if (!hasWorkflowLocaleText(n)) {
			return n;
		}
		const defaults =
			field === 'title'
				? this.workflowFormDefaults.getDocTitleDefaults(docType)
				: this.workflowFormDefaults.getDocDescriptionDefaults(docType);
		return mergeEmptyWorkflowLocalesFromDefaults(n, allowed, defaults);
	}

	// --- HOME ---

	/** Patch parcial `operations.HOME` desde el detalle. */
	public setFormHome(detail: IWorkflowDetailResponse, host: WorkflowFormDetailHost): any {
		const homeOperation = findHomeOperation(detail);
		if (homeOperation) {
			return {
				available: true,
				tecalis_policy_check: homeOperation.config?.tecalis_policy_check ?? true,
				policy_checks:
					homeOperation.config?.policy_checks?.map((checks: ITermsAndConditionsForm) =>
						host.formCheck({ mode: MenuItemAction.CREATE, form: checks }),
					) || [],
				description: this.normalizeDetailLocaleWithDefaults(
					homeOperation.config?.description,
					this.workflowFormDefaults.getHomeFormDescriptionDefaults(),
				),
				uuid: homeOperation.uuid,
			};
		}
		return {
			available: false,
		};
	}

	// --- FORM ---

	/** Patch parcial `operations.FORM` + rellena `formStepsArray`. */
	public setFormForm(detail: IWorkflowDetailResponse, host: WorkflowFormDetailHost): Record<string, unknown> {
		const formOperation = detail.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FORM) as
			| IFormOperation
			| undefined;

		if (!formOperation?.config) {
			return { available: false };
		}

		while (host.formStepsArray.length) {
			host.formStepsArray.removeAt(0);
		}

		const steps = readFormStepsFromConfig(formOperation.config as Record<string, unknown>);
		const allowed = this.langSvc.getAllowedLanguages();
		steps.forEach((step) => {
			const stepGroup = this.workflowsHelper.createFormStepGroup();
			const patchedStep = patchFormStepFromApi(step, allowed);
			stepGroup.patchValue({
				uuid: patchedStep['uuid'],
				step_id: patchedStep['step_id'],
				step_name: patchedStep['step_name'],
				step_description: patchedStep['step_description'],
				alert_msg: patchedStep['alert_msg'],
			});
			const fields = (patchedStep['fields'] as Record<string, unknown>[]) ?? [];
			fields.forEach((field) => {
				const fieldGroup = this.workflowsHelper.createFormFieldGroup();
				fieldGroup.patchValue(this.workflowsHelper.patchFormFieldValue(field));
				((field['options'] as { value: string; label: string }[]) ?? []).forEach((opt) => {
					const optGroup = this.workflowsHelper.formFormFieldOption;
					optGroup.patchValue(opt);
					(fieldGroup.get('options') as FormArray).push(optGroup);
				});
				(stepGroup.get('fields') as FormArray).push(fieldGroup);
			});
			host.formStepsArray.push(stepGroup);
		});

		const autofillFromApi = formOperation.config.autofill_from_ocr;
		return {
			available: true,
			tutorial: formOperation.config.tutorial ?? false,
			autofill_from_ocr: typeof autofillFromApi === 'boolean' ? autofillFromApi : null,
			uuid: formOperation.uuid,
		};
	}

	// --- OCR ---

	/** Patch parcial `operations.OCR` (fraud_scoring, docs, fallback). */
	public setFormOcr(detail: IWorkflowDetailResponse): any {
		const ocrOperation: IOcrOperation = detail.operations.find(
			(op: IOperation) => op.type === OPERATION_TYPE.OCR,
		) as IOcrOperation;
		if (ocrOperation) {
			const scoring = ocrOperation.config?.fraud_scoring;
			const fraudChecks = Object.fromEntries(
				OCR_FRAUD_CHECK_KEYS.map((key) => [key, mapOcrFraudCheckFromApi(scoring?.[key])]),
			);
			const obfuscationFields = ocrObfuscationSelectedFromFields(ocrOperation.config?.obfuscation?.fields);
			return {
				available: true,
				mode: ocrOperation.config?.mode,
				tutorial: ocrOperation.config?.tutorial,
				fraud_scoring: {
					aeat: scoring?.aeat ? 'true' : '',
					legal_age: scoring?.legal_age,
					expirated: scoring?.expirated,
					valid_country: Boolean(scoring?.valid_country),
					ocr_allowed_expiration: ocrOperation.config?.ocr_allowed_expiration,
					validate_address: ocrOperation.config?.validate_address,
					validate_team_number: ocrOperation.config?.validate_team_number,
					aml: scoring?.aml ? 'true' : '',
					pep: scoring?.pep ? 'true' : '',
					type: getOcrFraudScoringType(ocrOperation),
					...fraudChecks,
				},
				animations: ocrOperation.config?.setting?.show_guide,
				setting: {
					// NIF-829: capear a [1, 4] para que Validators.max(4) no bloquee Continuar en workflows legacy.
					total_tries: Math.min(4, Math.max(1, +ocrOperation.config?.setting?.total_tries || 2)),
					side_max_time: ocrOperation.config?.setting?.side_max_time,
					total_request: ocrOperation.config?.setting?.total_request,
				},
				dni_available: ocrOperation?.config?.allowed_docs?.dni || ocrOperation?.config?.not_allowed_docs?.dni,
				passport_available:
					ocrOperation?.config?.allowed_docs?.passport || ocrOperation?.config?.not_allowed_docs?.passport,
				nie_available: ocrOperation.config?.allowed_docs?.nie,
				dni_type: getOcrDocCountryType(ocrOperation, 'dni'),
				dni_countries: ocrOperation?.config?.allowed_docs?.dni || ocrOperation?.config?.not_allowed_docs?.dni,
				is_schengen_unlimited: resolveOcrSchengenUnlimited(ocrOperation.config),
				midni_validation_enabled: ocrOperation.config?.midni_validation_enabled,
				skip_pol_when_midni_authenticated: ocrOperation.config?.skip_pol_when_midni_authenticated,
				eudi_wallet_enabled: ocrOperation.config?.eudi_wallet_enabled ?? false,
				skip_pol_when_eudi_authenticated:
					ocrOperation.config?.skip_pol_when_eudi_authenticated === false ? 'false' : 'true',
				passport_type: getOcrDocCountryType(ocrOperation, 'passport'),
				passport_countries:
					ocrOperation.config?.allowed_docs?.passport || ocrOperation.config?.not_allowed_docs?.passport,
				required_check_nie: ocrOperation.config?.required_check_nie,
				scan_barcode: ocrOperation.config?.scan_barcode ?? false,
				obfuscation: {
					enabled: obfuscationFields.length > 0,
					fields: obfuscationFields,
					keep_unobfuscated_copy: ocrOperation.config?.obfuscation?.keep_unobfuscated_copy ?? true,
				},
				document_watermark: ocrOperation.config?.document_watermark ?? false,
				fallback: ocrOperation.config?.fallback?.type ?? 'auto',
				fallback_validation: ocrOperation.config?.fallback?.validation ?? 'auto',
				fallback_emails: ocrOperation.config?.fallback?.emails || '',
				uuid: ocrOperation.uuid,
			};
		}
		return {
			available: false,
		};
	}

	/**
	 * Infiera nivel de fraude OCR (delega en {@link getOcrFraudScoringType}).
	 */
	public getOcrFraudScoringType(ocrOperation: IOcrOperation): DEMAND_LEVEL {
		return getOcrFraudScoringType(ocrOperation);
	}

	/**
	 * Tipo de lista de países para DNI o pasaporte (delega en {@link getOcrDocCountryType}).
	 */
	public getOcrDocCountryType(ocrOperation: IOcrOperation, type: 'dni' | 'passport'): OCR_DOC_COUNTRY_TYPE {
		return getOcrDocCountryType(ocrOperation, type);
	}

	// --- POL ---

	/** Patch parcial `operations.POL` (setting + fraud_scoring). */
	public setFormPol(detail: IWorkflowDetailResponse): any {
		const polOperation: IPolOperation = detail.operations.find(
			(op: IOperation) => op.type === OPERATION_TYPE.POL,
		) as IPolOperation;
		if (polOperation) {
			// NIF-773 / NIF-476: hidratar con sanitize (total_test≤3, sin tests verticales deprecados)
			// para que Validators.max(3) no deje POL inválido y bloquee Continuar.
			const sanitizedSetting = sanitizePolSetting(polOperation.config.setting);
			const polScore = Number(sanitizedSetting.scoring);
			const scoringThreshold = mapPolScoreToFormThreshold(polScore);
			return {
				available: true,
				mode: polOperation.config?.mode,
				tutorial: polOperation.config?.tutorial,
				type: this.workflowsHelper.selectPolDemandLevel(polOperation.config.setting),
				setting: {
					total_tries: +sanitizedSetting.total_tries,
					type: String(sanitizedSetting.type),
					total_test: sanitizedSetting.total_test,
					scoring: scoringThreshold.scoring,
					scoring_custom: scoringThreshold.scoring_custom,
					available_tests: [POL_TEST.MIDDLE_FACE, ...(sanitizedSetting.available_tests ?? [])],
					max_time: polOperation.config?.max_time || 300,
				},
				fraud_scoring: {
					facial_recognition: polOperation.config.fraud_scoring?.facial_recognition,
					face_match: polOperation.config.fraud_scoring?.face_match,
					percentage_similarity: polOperation.config.fraud_scoring?.percentage_similarity,
					anti_spoofing: polOperation.config.fraud_scoring?.anti_spoofing,
				},
				fallback: polOperation.config.fallback?.type ?? 'auto',
				fallback_validation: polOperation.config.fallback?.validation ?? 'auto',
				fallback_emails: polOperation.config.fallback?.emails || '',
				uuid: polOperation.uuid,
			};
		}
		return {
			available: false,
		};
	}

	// --- ATTORNEY_POWERS ---

	/** Patch parcial `operations.ATTORNEY_POWERS` inferido de DOCUMENTS/CONNECTIONS SCRIPTURE. */
	public setAttorneyPowersDocuments(detail: IWorkflowDetailResponse): any {
		return mapAttorneyPowersFormPatch(findAttorneyPowersSources(detail));
	}

	// --- DOCUMENTS ---

	/** Patch parcial `operations.DOCUMENTS` + `formDoc` por fila (sin SCRIPTURE). */
	public setFormDocuments(detail: IWorkflowDetailResponse, host: WorkflowFormDetailHost): any {
		const allowed = this.langSvc.getAllowedLanguages();
		const docsOperation = findNonScriptureDocumentsOperation(detail);
		const connectionsWithoutEval = findConnectionsWithoutEval(detail);

		if (docsOperation || connectionsWithoutEval?.length) {
			let docs: any[] =
				docsOperation?.config?.docs?.map((doc: IDocumentsDocs) => {
					const docInfo = this.workflowsHelper.docsInfo.find((d: IDocInfo) => d.type === doc.type);
					const isGeneric = doc.type === DOCS_TYPE.GENERIC;
					return {
						...doc,
						match_data: normalizeDocsMatchData(doc.match_data),
						method: normalizeDocumentMethodForForm(doc.method as string),
						icon: docInfo?.icon,
						docInfo: docInfo,
						title: isGeneric
							? mergeEmptyWorkflowLocalesFromDefaults(
									mergeGenericDocLocaleField(
										doc.title,
										allowed,
										this.workflowFormDefaults.getGenericDocTitleDefaults(),
									),
									allowed,
									this.workflowFormDefaults.getGenericDocTitleDefaults(),
								)
							: this.mergeDocRowTitleOrDescriptionIfAnyFilled(docInfo?.title, doc.type, 'title'),
						description: isGeneric
							? mergeEmptyWorkflowLocalesFromDefaults(
									mergeGenericDocLocaleField(
										doc.description,
										allowed,
										this.workflowFormDefaults.getGenericDocDescriptionDefaults(),
									),
									allowed,
									this.workflowFormDefaults.getGenericDocDescriptionDefaults(),
								)
							: this.mergeDocRowTitleOrDescriptionIfAnyFilled(
									docInfo?.description,
									doc.type,
									'description',
								),
						instructions: this.mergeDocInstructionsIfAnyFilled(doc.instructions, doc.type),
						instructions_photo: this.mergeDocInstructionsIfAnyFilled(doc.instructions_photo, doc.type),
					};
				}) || [];
			connectionsWithoutEval.forEach((c: IConnectionOperation) => {
				const docInfo = this.workflowsHelper.docsInfo.find((d: IDocInfo) => d.type === c.config.type);
				const rowType = String(docInfo?.confRecommended?.connection_type ?? '');
				docs.push({
					type: docInfo?.confRecommended?.connection_type,
					required: true,
					method: ['manual', 'photo'],
					total_tries: c.config.total_tries,
					min: null,
					max: null,
					instructions: this.mergeDocInstructionsIfAnyFilled('', rowType),
					instructions_photo: this.mergeDocInstructionsIfAnyFilled('', rowType),
					description: this.mergeDocRowTitleOrDescriptionIfAnyFilled(
						docInfo?.description,
						rowType,
						'description',
					),
					preview_validation: docInfo?.confRecommended.preview_validation,
					match_data: normalizeDocsMatchData(docInfo?.confRecommended.match_data),
					icon: docInfo?.icon,
					docInfo: docInfo,
					title: this.mergeDocRowTitleOrDescriptionIfAnyFilled(docInfo?.title, rowType, 'title'),
				});
			});
			docs = docs || [];
			docs.forEach((doc: IDocumentsDocs) => {
				host.formDoc({ form: doc });
			});
			return {
				available: true,
				tutorial: docsOperation?.config?.tutorial || true,
				custom_title: hasWorkflowLocaleText(docsOperation?.config?.title),
				title: this.normalizeDetailLocaleWithDefaults(
					docsOperation?.config?.title ?? '',
					this.workflowFormDefaults.getDocumentsSectionTitleDefaults(),
				),
				docs: docs,
				uuid: docsOperation?.uuid || connectionsWithoutEval?.at(0)?.uuid,
			};
		}
		return {
			available: false,
		};
	}

	// --- CONNECTIONS ---

	/** Patch parcial `operations.CONNECTIONS` (con evaluation / CHOOSE). */
	public setFormConnections(detail: IWorkflowDetailResponse): any {
		return mapConnectionsFormPatch(detail);
	}

	// --- FINISH ---

	/** Patch parcial `operations.FINISH` (mensajes, redirects, callbacks). */
	public setFormFinish(detail: IWorkflowDetailResponse): any {
		const finishOperation = findFinishOperation(detail);
		if (finishOperation) {
			const allowedLocales = this.langSvc.getAllowedLanguages();

			return {
				available: true,
				loading: finishOperation.config?.loading,
				show_ko_reason: finishOperation.config?.show_ko_reason ?? true,
				loading_text: normalizeWorkflowLocaleText(finishOperation.config?.loading_text, allowedLocales),
				ok_title: normalizeWorkflowLocaleText(finishOperation.config?.ok_title, allowedLocales),
				ok_description: normalizeWorkflowLocaleText(finishOperation.config?.ok_description, allowedLocales),
				ko_title: normalizeWorkflowLocaleText(finishOperation.config?.ko_title, allowedLocales),
				ko_description: normalizeWorkflowLocaleText(finishOperation.config?.ko_description, allowedLocales),
				cancelled_title: normalizeWorkflowLocaleText(finishOperation.config?.cancelled_title, allowedLocales),
				cancelled_description: normalizeWorkflowLocaleText(
					finishOperation.config?.cancelled_description,
					allowedLocales,
				),
				pending_title: normalizeWorkflowLocaleText(finishOperation.config?.pending_title, allowedLocales),
				pending_description: normalizeWorkflowLocaleText(
					finishOperation.config?.pending_description,
					allowedLocales,
				),
				redirect: normalizeFinishRedirectFromApi(finishOperation.config?.redirect),
				redirect_ko: normalizeFinishRedirectFromApi(finishOperation.config?.redirect_ko),
				redirect_cancelled: normalizeFinishRedirectFromApi(finishOperation.config?.redirect_cancelled),
				callback: normalizeFinishRedirectFromApi(finishOperation.config?.callback),
				callback_ko: normalizeFinishRedirectFromApi(finishOperation.config?.callback_ko),
				callback_cancelled: normalizeFinishRedirectFromApi(finishOperation.config?.callback_cancelled),
				uuid: finishOperation.uuid,
			};
		}
		return undefined;
	}
}
